// AUTO-DERIVED from telegram-bridge/downloads/09_symptom_cluster_map.json (round 7,
// fix 48/56): 100 catalog symptoms -> 20 clinical clusters. The provider report holds
// NO hardcoded cluster/symptom list; it derives its cluster card entirely from here, so
// a new catalog symptom appears with no report code change. `cat` stays the rating-form
// category (7); `cluster` is the finer grouping the report renders (20).

export const CAT_COLOR: Record<string, string> = {
  "vaso": "#a75223",
  "body": "#7a4a3a",
  "met": "#2e7d6b",
  "mood": "#6b4fa0",
  "skin": "#8b6433",
  "sens": "#256cc1",
  "uro": "#9c3f6b"
};

// Display/tiebreak order (the report re-sorts clusters by their top score each render).
export const CLUSTER_KEYS: string[] = [
  "heatCold",
  "sleepFatigue",
  "painStiffness",
  "skinNerveSensations",
  "strengthMovement",
  "energyFatigue",
  "digestionGut",
  "heartBalance",
  "appetiteWeight",
  "moodEmotion",
  "motivationConfidence",
  "focusMemory",
  "skinCluster",
  "hairScalpNails",
  "sensitivities",
  "headSenses",
  "mouthTasteTeeth",
  "vaginalIntimacy",
  "urinaryPelvic",
  "breasts"
];

// clusterKey -> rating-form cat (drives the cluster colour).
export const CLUSTER_CAT: Record<string, string> = {
  "heatCold": "vaso",
  "sleepFatigue": "vaso",
  "painStiffness": "body",
  "skinNerveSensations": "body",
  "strengthMovement": "body",
  "energyFatigue": "met",
  "digestionGut": "met",
  "heartBalance": "met",
  "appetiteWeight": "met",
  "moodEmotion": "mood",
  "motivationConfidence": "mood",
  "focusMemory": "mood",
  "skinCluster": "skin",
  "hairScalpNails": "skin",
  "sensitivities": "skin",
  "headSenses": "sens",
  "mouthTasteTeeth": "sens",
  "vaginalIntimacy": "uro",
  "urinaryPelvic": "uro",
  "breasts": "uro"
};

// symptom key -> clusterKey.
export const SYMPTOM_TO_CLUSTER: Record<string, string> = {
  "hot_flashes": "heatCold",
  "night_sweats": "heatCold",
  "chills_cold_spells": "heatCold",
  "heat_intolerance": "heatCold",
  "cold_hand_foot_sensitivity": "heatCold",
  "insomnia_falling_asleep": "sleepFatigue",
  "insomnia_staying_asleep": "sleepFatigue",
  "early_awakening": "sleepFatigue",
  "unrefreshing_sleep": "sleepFatigue",
  "daytime_sleepiness": "sleepFatigue",
  "restless_legs": "sleepFatigue",
  "hot_feet": "heatCold",
  "snoring": "sleepFatigue",
  "joint_pain": "painStiffness",
  "stiffness": "painStiffness",
  "muscle_aches": "painStiffness",
  "back_pain": "painStiffness",
  "neck_shoulder_tension": "painStiffness",
  "hand_wrist_discomfort": "painStiffness",
  "foot_ankle_discomfort": "painStiffness",
  "cramps_non_period": "painStiffness",
  "tingling_or_numbness": "skinNerveSensations",
  "electric_shock_sensations": "skinNerveSensations",
  "grip_strength_loss": "strengthMovement",
  "crawling_skin_sensation": "skinNerveSensations",
  "shoulder_restriction": "strengthMovement",
  "general_weakness": "strengthMovement",
  "energy_dips": "energyFatigue",
  "bloating": "digestionGut",
  "constipation": "digestionGut",
  "diarrhea_gas": "digestionGut",
  "heartburn_reflux": "digestionGut",
  "heart_palpitations": "heartBalance",
  "dizziness_imbalance": "heartBalance",
  "sugar_cravings": "appetiteWeight",
  "weight_gain": "appetiteWeight",
  "deep_fatigue": "energyFatigue",
  "nausea": "digestionGut",
  "abdominal_pain": "digestionGut",
  "appetite_loss": "appetiteWeight",
  "salt_cravings": "appetiteWeight",
  "abdominal_fat": "appetiteWeight",
  "anxiety": "moodEmotion",
  "low_mood": "moodEmotion",
  "mood_swings": "moodEmotion",
  "irritability": "moodEmotion",
  "stress_sensitivity": "moodEmotion",
  "motivation_drop": "motivationConfidence",
  "brain_fog": "focusMemory",
  "difficulty_concentrating": "focusMemory",
  "forgetfulness": "focusMemory",
  "slow_recall": "focusMemory",
  "anger": "moodEmotion",
  "dread": "moodEmotion",
  "sudden_tears": "moodEmotion",
  "self_confidence_drop": "motivationConfidence",
  "social_withdrawal": "motivationConfidence",
  "social_unease": "motivationConfidence",
  "clumsiness": "focusMemory",
  "restlessness": "moodEmotion",
  "adult_acne": "skinCluster",
  "dry_skin": "skinCluster",
  "itchy_sensitive_skin": "skinCluster",
  "hair_thinning": "hairScalpNails",
  "brittle_nails": "hairScalpNails",
  "body_odor_change": "skinCluster",
  "hair_texture_change": "hairScalpNails",
  "increased_facial_hair": "hairScalpNails",
  "skin_dark_patches": "skinCluster",
  "skin_rashes": "skinCluster",
  "scalp_sensitivity": "hairScalpNails",
  "allergy_increase": "sensitivities",
  "frequent_cold_symptoms": "sensitivities",
  "dry_eyes_gritty_feeling": "headSenses",
  "vision_fluctuation": "headSenses",
  "tinnitus_ringing": "headSenses",
  "burning_mouth": "mouthTasteTeeth",
  "headaches_migraines": "headSenses",
  "headaches_non_migraine": "headSenses",
  "migraine_with_aura": "headSenses",
  "migraine_without_aura": "headSenses",
  "itchy_ears": "headSenses",
  "taste_change": "mouthTasteTeeth",
  "dry_mouth": "mouthTasteTeeth",
  "metallic_taste": "mouthTasteTeeth",
  "phantom_smells": "headSenses",
  "noise_sensitivity": "headSenses",
  "light_sensitivity": "headSenses",
  "tooth_sensitivity": "mouthTasteTeeth",
  "gum_tenderness": "mouthTasteTeeth",
  "vaginal_dryness": "vaginalIntimacy",
  "vaginal_discomfort": "vaginalIntimacy",
  "itching_irritation_vulvar": "vaginalIntimacy",
  "pain_with_intercourse": "vaginalIntimacy",
  "reduced_libido": "vaginalIntimacy",
  "urinary_frequency": "urinaryPelvic",
  "urinary_urgency": "urinaryPelvic",
  "urinary_leakage": "urinaryPelvic",
  "recurrent_utis": "urinaryPelvic",
  "pelvic_heaviness": "urinaryPelvic",
  "breast_tenderness": "breasts",
  "breast_swelling": "breasts",
  "breast_stinging": "breasts"
};

// The report iterates this in place of the old 5-entry hardcoded list. symptomKeys is
// every catalog symptom assigned to the cluster; the render filters to those present.
export const CLUSTER_VIEW_DEFS: Array<{ key: string; color: string; symptomKeys: string[] }> =
  CLUSTER_KEYS.map((key) => ({
    key,
    color: CAT_COLOR[CLUSTER_CAT[key]] || '#171716',
    symptomKeys: Object.keys(SYMPTOM_TO_CLUSTER).filter((s) => SYMPTOM_TO_CLUSTER[s] === key),
  }));
